import classNames from 'classnames';
import * as React from 'react';
import { CSSTransition } from 'react-transition-group';
import { Button } from '~/Partials/Button/Button';
import { List } from '~/Partials/List/List';
import { SvgIcon } from '~/Partials/SvgIcon/SvgIcon';
import { animationDurationMs } from '~/Utils/AnimationsUtils';
import { useMultiselect } from '~/Utils/Hooks/useMultiselect';
import styles from './StudyFilterDropdown.module.css';
import { StudyFilterDropdownPopup } from './StudyFilterDropdownPopup';
import type { StudyFilterDropdownVariant } from './StudyFilterDropdownVariant';
import type { StudyFilterItem } from './types';

export interface StudyFilterDropdownProps {
    items: StudyFilterItem[];
    selectedItems: StudyFilterItem[];
    placeholder: string;
    selectLabel: string;
    onSelectionChanged: (items: StudyFilterItem[]) => void;
    initialIsOpen?: boolean;
    variant?: StudyFilterDropdownVariant;
}

export const StudyFilterDropdown: React.FC<StudyFilterDropdownProps> = props => {
    const {
        items,
        selectedItems,
        selectLabel,
        placeholder,
        onSelectionChanged,
        initialIsOpen,
        variant = 'dark',
    } = props;

    const [popupAnchor, setPopupAnchor] = React.useState<HTMLElement | null>(null);

    const transitionRef = React.useRef<HTMLDivElement>(null);

    const { isOpen, highlightedIndex, getToggleButtonProps, getLabelProps, getMenuProps, getItemProps } =
        useMultiselect({
            initialIsOpen,
            items,
            itemToString: item => item?.label || '',
            selectedItems,
            onSelectionChanged,
        });

    const renderOptionItem = (option: StudyFilterItem, index: number) => {
        const isSelected = selectedItems.includes(option);
        const isHighlighted = highlightedIndex === index;

        return (
            <li
                key={option.id}
                {...getItemProps(option)}
                className={classNames(styles.Item, isHighlighted && styles['Item--highlighted'])}
            >
                <SvgIcon iconName={isSelected ? 'square-fill' : 'square'} className={styles.ItemIcon} />
                {option.label}
            </li>
        );
    };

    const getSelectedLabel = () => {
        switch (selectedItems.length) {
            case 0:
                return placeholder;
            case 1:
                return selectedItems[0].label;
            default:
                return `${selectedItems[0].label}... +${selectedItems.length - 1}`;
        }
    };

    return (
        <div className={classNames(styles.StudyFilterDropdown, styles[`Variant--${variant}`])}>
            <label {...getLabelProps()} className="visuallyHidden">
                {selectLabel}
            </label>
            <div ref={setPopupAnchor}>
                <Button
                    {...getToggleButtonProps()}
                    title={selectLabel}
                    className={classNames(styles.ToggleButton, isOpen && styles['ToggleButton--open'])}
                >
                    <span>{getSelectedLabel()}</span>
                    <SvgIcon className={styles.ArrowIcon} iconName="chevron-down" />
                </Button>
            </div>
            <StudyFilterDropdownPopup
                isOpen={isOpen}
                popupAnchor={popupAnchor}
                numberOfSelections={selectedItems.length}
            >
                <CSSTransition nodeRef={transitionRef} in={isOpen} timeout={animationDurationMs} appear>
                    <div ref={transitionRef} className={styles.AnimatedDropdown}>
                        <List {...getMenuProps()} items={items} renderItem={renderOptionItem} className={styles.List} />
                    </div>
                </CSSTransition>
            </StudyFilterDropdownPopup>
        </div>
    );
};
